// SKFitter (Quantum Materials): the desktop's screens as data - forms, set-up, and the API calls behind them.
import { useQuery } from "@tanstack/react-query";
import { get, post, type StoredFile } from "./api";
import type { LimitInfo } from "./transport";

export interface FileRole { role: string; label: string; name: string; required: boolean }

export interface SkForms {
  codes: { value: string; label: string }[];
  files: Record<string, FileRole[]>;
  orbital_layout: { family: string; orbitals: string[] }[];
  orbital_tips: Record<string, string>;
  presets: Record<string, string[]>;
  fit_options: FitOptions;
  setup_defaults: Omit<Cfg, "orbital_config" | "magnetic_orbitals" | "include_soc">;
  param_groups: string[];
  wannierberri: { ok: boolean; version: string | null; flavour: string | null; message: string };
}

export interface Cfg {
  orbital_config: Record<string, string[]>;
  magnetic_orbitals: Record<string, string[]>;
  n_bands_below: number;
  n_bands_above: number;
  n_neighbors: number;
  symprec: number;
  fit_eta: boolean;
  fixed_eta: number;
  // how the hoppings depend on the bond length: "decay" = one integral per bond type, V0 (d0/r)^η (default);
  // "shell" = an independent integral per neighbour shell (v_<type>_<n>), no η fitted
  hopping_mode?: "decay" | "shell";
  include_soc: boolean;
  non_orthogonal: boolean;
  model_states_only?: boolean;     // fit only the DFT states of the model's orbitals (skip vacuum / other-orbital states)
  tb_nelect: number | null;
  scissor?: number;                // eV: the DFT conduction bands are shifted by this before fitting (0 = none)
  // elements whose sites get the on-site terms their symmetry allows beyond one energy per orbital group
  // (e_mix_* parameters: e.g. d E1-E2 mixing on a trigonal site, s-dz2 without inversion); [] = none
  onsite_mixing?: string[];
}

export interface FitOptions {
  method: "ls" | "de";
  n_starts: number;
  maxiter: number;
  popsize: number;
  k_stride: number;
  refine: boolean;
  refine_max_nfev: number;
  seed: number | null;
  add_weight_at_symmetry_points: boolean;
  symmetry_weight: number;
  symmetry_width: number;
  add_weight_at_fermi_bands: boolean;
  fermi_weight: number;
  fermi_window: number;
}

export interface DftRef { code: string; files: Record<string, string>; cache_file: string }

export interface ElementInfo { symbol: string; count: number; Z: number; default_orbitals: string[]; default_magnetic: string[] }

export interface DftResult {
  meta: {
    formula: string; spacegroup: string; efermi: number; nelect: number | null; soc: boolean; magnetic: boolean;
    elements: string[]; nk: number; nbands: number; orbital_names: string[]; family_cols: Record<string, number[]>;
    has_projection: boolean; dft_code: string; lattice: number[][];
    moments?: number[] | null;   // initial moment per site (VASP MAGMOM; z of a non-collinear run)
  };
  code_label: string;
  kpath_mode: string;
  warnings: string[];
  elements: ElementInfo[];
  seed: string;
  dft: DftRef;
}

export interface SegTrace { color: string; width: number; x: (number | null)[]; y: (number | null)[] }

export interface BandView {
  panels: { title: string; traces: SegTrace[] }[];
  legend: { label: string; color: string }[];
  colorbar: { label: string; max: number; colors: string[] } | null;
  note: string;
  window: [number, number];
  x_range: [number, number];
  ticks: number[];
  tick_labels: string[] | null;
}

export interface SetupView {
  orbital_error: string;
  orbital_warning?: string;      // some elements have no orbitals: allowed, their atoms are left out
  basis: number;
  dim: number;
  window: { first: number | null; last: number | null; count: number; lo: number; hi: number; vbm: number } | null;
  plot: { fitted: SegTrace[]; others: SegTrace[]; window: [number, number]; x_range: [number, number]; ticks: number[];
    tick_labels: string[] | null } | null;
  limit: LimitInfo | null;
  size_limit?: LimitInfo | null;     // the material / model is larger than the plan allows (atoms, shells, model size)
  opts_error: string;
}

export interface ModelResult {
  param_keys: string[];
  initial: Record<string, number>;
  bounds: Record<string, [number, number]>;
  groups: Record<string, string>;
  info: { base_dim: number; total_dim: number; n_params: number; spacegroup: string; n_target_bands: number; nk: number;
    soc: boolean; magnetic: boolean; tb_nelect: number; n_neighbors: number; n_pairs: number;
    moments?: string };
  cfg: Cfg;
  env?: { d0: number; hopping_mode?: string; shell_distances?: number[] };   // the built model (starting values)
}

export interface ParamRow { group: string; key: string; value: number; lo: number; hi: number; at_bound: boolean; initial: number | null }

export interface FitResult {
  x: number[]; ticks: number[]; tick_labels: string[] | null;
  e_dft: (number | null)[][]; e_tb: number[][];         // [band][k]; null: a DFT state left out of the fit
  rmse_meV: number;
  report: { all?: number | null; near_fermi?: number | null; symmetry_points?: number | null };
  cancelled: boolean; stop_reason: string; non_orthogonal: boolean;
  params: Record<string, number>;
  rows: ParamRow[];
  info: ModelResult["info"];
  cfg: Cfg;
  seed: string;
  convergence: { n: number[]; rmse: number[] } | null;
  files?: Record<string, string>;
  loaded_from?: string;
  param_file?: string;
}

/** A table row as edited: raw text in each cell. */
export interface TableRow { key: string; group: string; start: string; lo: string; hi: string; src?: "set" | "default" }

/** What the project remembers (project.settings.skfitter). */
export interface SkMemory {
  code: string;
  files: Record<string, string>;
  seed: string;
  // DFT data from uploaded files, or imported from a run of a DFT project ("generate": made with SKFitter's former
  // "Generate with Quantum ESPRESSO", now the DFT module)
  dft_source?: "upload" | "import" | "generate";
  qe?: { recipe: Record<string, any>; template?: string | null; changed?: boolean };   // (former "Generate": its recipe)
  imported_from?: { project_id: string; job_id: string };   // the DFT run the Quantum ESPRESSO files were copied from
  auto_read?: boolean;             // read the files as soon as the project opens (made with "Use in SKFitter")
  dft_job?: string;
  cfg?: Cfg;
  opts?: FitOptions;
  model_job?: string;
  start_set?: string;              // the parameter set the starting values come from ("default": the model's defaults)
  table?: TableRow[];
  table_sig?: string;
  fit_job?: string;
  step?: number;
  export?: { job: string; fit_job: string };
  props_job?: string;             // the band check (or a property run made before M3f, still viewable)
  // an illustrative project (examples/skfitter/<id>): its id, the starting values it fixes on top of the parameter
  // set ({parameter: [start, lower, upper]}), the Wannier export k-mesh and what it continues with in Properties
  example?: string;
  overrides?: Record<string, [number, number, number]>;
  export_kmesh?: number[];
  props_continue?: string;
}

export function useSkForms() {
  return useQuery({ queryKey: ["skfitter-forms"], queryFn: () => get<SkForms>("/skfitter/forms"), staleTime: Infinity });
}

export const bandView = (body: { job_id: string; color_by: string; atoms: string; orbital: string; emin: number; emax: number; spin_view: string }) =>
  post<BandView>("/skfitter/bands", body);
export const setupView = (job_id: string, cfg: Cfg, opts: FitOptions) => post<SetupView>("/skfitter/setup", { job_id, cfg, opts });
/** Orbital character of the bands selected for the fit at one k-point of the DFT path (DFT projections). */
export interface CharacterView {
  points: { label: string; index: number; frac: number[] }[];        // high-symmetry points of the DFT path
  k: { index: number; frac: number[]; label: string | null; asked: number[] | null; distance: number; x: number } | null;
  channels: string[];                                                 // "<element> <family>", "other", "unassigned"
  rows: { band: number; E: number; spin: "up" | "down" | null; weights: Record<string, number>; total: number }[];
  error: string;
}
export const characterView = (job_id: string, cfg: Cfg, sel: { point?: string; k?: number[] }) =>
  post<CharacterView>("/skfitter/character", { job_id, cfg, ...sel });

/** Γ for G in a k-path label ("G'" -> "Γ′"). */
export const kLabel = (l: string) => l.replace(/^G(?=$|'|\d)/, "Γ").replace(/'/g, "′");

/** Horizontal stacked bars: one bar per fitted band (E - E_F), one colour per element and orbital family. */
export function characterFigure(cv: CharacterView, colors: string[]) {
  const rows = cv.rows;
  const name = (r: CharacterView["rows"][number]) => `${r.E >= 0 ? "+" : ""}${r.E.toFixed(3)} eV${r.spin ? (r.spin === "up" ? " ↑" : " ↓") : ""} · ${r.band + 1}`;
  const y = rows.map(name);
  let ci = 0;
  const data = cv.channels.map((c) => ({
    type: "bar", orientation: "h", name: c, y, x: rows.map((r) => r.weights[c] ?? 0),
    marker: { color: c === "unassigned" ? "#d4d2ca" : c === "other" ? "#9a9992" : colors[ci++ % colors.length],
      line: { color: "white", width: 0.5 } },
    hovertemplate: `${c}: %{x:.3f}<br>%{y}<extra></extra>`,
  }));
  const k = cv.k;
  const where = k ? (k.label ? kLabel(k.label) : `k = (${k.frac.map((v) => v.toFixed(3)).join(", ")})`) : "";
  return {
    data,
    layout: {
      barmode: "stack", bargap: 0.18,
      title: { text: `Orbital character at ${where}`, font: { size: 13 } },
      xaxis: { title: { text: "orbital weight (DFT projections)" }, range: [0, 1.02], zeroline: false },
      yaxis: { title: { text: "band at this k: E − E<sub>F</sub> · band number" }, autorange: true, type: "category", automargin: true,
        tickfont: { size: 10 } },
      legend: { orientation: "h", y: -0.18, x: 0, font: { size: 10 }, traceorder: "normal" },
      margin: { l: 10, r: 10, t: 40, b: 60 },
    },
  };
}

export const readParamFile = (file_id: string) =>
  post<{ ok: boolean; error?: string; params?: Record<string, number> }>("/skfitter/paramfile", { file_id });

// -- helpers ---------------------------------------------------------------------------------------------------
const GUESS: Record<string, RegExp> = {
  vasprun: /vasprun.*\.xml$/i,
  kpoints: /^kpoints/i,
  qe_xml: /^(?!vasprun).*\.xml$/i,
  qe_bands_in: /band.*\.(in|pwi|inp)$|\.(pwi)$/i,
  qe_scf: /scf.*\.(out|log|xml)$/i,
  qe_projwfc: /projwfc_up$/i,
  qe_projwfc_down: /projwfc_down$/i,
};

/** Which uploaded file is which DFT file, guessed from the names (newest first); keeps choices already made. */
export function guessFiles(roles: FileRole[], uploads: StoredFile[], current: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  const byNewest = [...uploads].sort((a, b) => b.created_at.localeCompare(a.created_at));
  for (const r of roles) {
    if (current[r.role] && uploads.some((f) => f.id === current[r.role])) { out[r.role] = current[r.role]; continue; }
    const re = GUESS[r.role];
    const hit = re && byNewest.find((f) => re.test(f.name) && !Object.values(out).includes(f.id));
    if (hit) out[r.role] = hit.id;
  }
  return out;
}

export function seedFrom(name: string): string {
  return name.trim().replace(/[^A-Za-z0-9_.-]+/g, "_").replace(/^[._-]+|[._-]+$/g, "").replace(/-/g, "_").toLowerCase() || "fit";
}

/** Magnetic orbitals can be chosen: spin-split bands, or a non-collinear run with moments in its MAGMOM (one list of
 * spinor bands, so magnetism does not show as up / down bands). Mirrors skfitter_jobs.magnetic_possible. */
export function magneticPossible(m: DftResult["meta"]): boolean {
  return m.magnetic || (m.soc && (m.moments ?? []).some((v) => Math.abs(v) >= 0.5));
}

/** The set-up the desktop starts from for this DFT data (its SetupPage.populate). */
export function defaultCfg(forms: SkForms, dft: DftResult): Cfg {
  return {
    ...forms.setup_defaults,
    orbital_config: Object.fromEntries(dft.elements.map((e) => [e.symbol, [...e.default_orbitals]])),
    magnetic_orbitals: dft.meta.magnetic ? Object.fromEntries(dft.elements.map((e) => [e.symbol, [...e.default_magnetic]])) : {},
    include_soc: dft.meta.soc,
  };
}

/** The part of the set-up the parameter list depends on (the desktop's config_signature). */
export function cfgSignature(cfg: Cfg): string {
  const keep = ["orbital_config", "magnetic_orbitals", "n_bands_below", "n_bands_above", "n_neighbors", "symprec", "fit_eta",
    "fixed_eta", "include_soc", "non_orthogonal", "tb_nelect"] as const;
  const sig: unknown[] = keep.map((k) => [k, cfg[k]]);
  if ((cfg.hopping_mode ?? "decay") !== "decay") sig.push(["hopping_mode", cfg.hopping_mode]);   // decay: as before
  if (cfg.onsite_mixing?.length) sig.push(["onsite_mixing", [...cfg.onsite_mixing].sort()]);         // none: as before
  return JSON.stringify(sig);
}

export function tableFromModel(m: ModelResult): TableRow[] {
  return m.param_keys.map((k) => ({ key: k, group: m.groups[k] ?? "", start: fmt(m.initial[k]), lo: fmt(m.bounds[k][0]), hi: fmt(m.bounds[k][1]) }));
}

// -- starting values from published parameter sets (DFTB.org, NIST, Harrison, NRL) ------------------------------
export interface ParamSetInfo {
  id: string; name: string; kind: "dftb" | "nist" | "harrison" | "nrl"; description: string; source: string; url: string;
  licence: string; citation: string; version: string; notes: string; elements: string[];
  provides: { onsite: boolean; hopping: boolean; overlap: boolean };
  coverage?: { covered: string[]; missing: string[] };
}
export interface StartValues {
  set: string; name: string; initial: Record<string, number>; bounds: Record<string, [number, number]>;
  source: Record<string, "set" | "default">; shift: number; alignment: "dft" | "fermi" | "none";
  coverage: Record<string, [number, number]>; notes: string[]; citation: string; licence: string;
}
export const DEFAULT_START = "default";

export function listParamSets(elements: string[]) {
  return get<ParamSetInfo[]>(`/skfitter/paramsets?elements=${encodeURIComponent(elements.join(","))}`);
}

export function startValues(modelJobId: string, setId: string) {
  return post<StartValues>("/skfitter/startvalues", { model_job_id: modelJobId, set_id: setId });
}

/** The model's parameter table with a set's starting values and bounds (defaults where the set has nothing). */
export function tableFromStart(m: ModelResult, s: StartValues): TableRow[] {
  return m.param_keys.map((k) => {
    const b = s.bounds[k] ?? m.bounds[k];
    return { key: k, group: m.groups[k] ?? "", start: fmt(s.initial[k] ?? m.initial[k]), lo: fmt(b[0]), hi: fmt(b[1]),
      src: s.source[k] === "set" ? "set" : "default" };
  });
}

/** The starting values / bounds an illustrative project fixes, on top of the table (rows of other models are kept). */
export function applyOverrides(rows: TableRow[], overrides?: Record<string, [number, number, number]>): TableRow[] {
  if (!overrides) return rows;
  return rows.map((r) => {
    const o = overrides[r.key];
    return o ? { ...r, start: fmt(o[0]), lo: fmt(o[1]), hi: fmt(o[2]) } : r;
  });
}

/** "3 of 4 on-site energies, 5 of 5 integrals" */
export function coverageText(c: StartValues["coverage"]): string {
  const names: Record<string, string> = { onsite: "on-site energies", hopping: "integrals", overlap: "overlaps", decay: "decay exponents" };
  return Object.entries(c).map(([g, [n, t]]) => `${n} of ${t} ${names[g] ?? g}`).join(", ");
}

export const fmt = (v: number | null | undefined) => (v == null || !Number.isFinite(v) ? "" : String(Number(v.toPrecision(6))));

/** Starting values and bounds from the table (the desktop's read_table): errors per row, starts clipped into bounds. */
export function readTable(rows: TableRow[]): { initial: Record<string, number>; bounds: Record<string, [number, number]>; errors: Record<string, string> } {
  const initial: Record<string, number> = {};
  const bounds: Record<string, [number, number]> = {};
  const errors: Record<string, string> = {};
  for (const r of rows) {
    const [v, lo, hi] = [r.start, r.lo, r.hi].map((t) => (t.trim() === "" ? NaN : Number(t)));
    if (![v, lo, hi].every(Number.isFinite)) { errors[r.key] = "start, lower and upper must be numbers"; continue; }
    if (!(lo < hi)) { errors[r.key] = "the lower bound must be smaller than the upper bound"; continue; }
    initial[r.key] = Math.min(Math.max(v, lo), hi);
    bounds[r.key] = [lo, hi];
  }
  return { initial, bounds, errors };
}

/** Put a saved fit's values into the "Start" column; returns the new rows and how many were taken. */
export function applySaved(rows: TableRow[], params: Record<string, number>, env?: ModelResult["env"]): [TableRow[], number] {
  const all = { ...shellStartsFromDecay(rows.map((r) => r.key), params, env), ...params };
  let n = 0;
  const next = rows.map((r) => (r.key in all ? (n++, { ...r, start: fmt(all[r.key]) }) : r));
  return [next, n];
}

/** A per-shell model started from a decay-law fit: V_n = V0 (d0/r_n)^η for every v_/s_<type>_<n> the saved fit lacks
 *  (V0 = <type>_0, η = eta_<type> / eta_s_<type>, 2.5 if absent) - the decay-law integral at each shell distance. */
export function shellStartsFromDecay(keys: string[], params: Record<string, number>, env?: ModelResult["env"]): Record<string, number> {
  const dist = env?.shell_distances ?? [];
  if (env?.hopping_mode !== "shell" || !dist.length || !(env.d0 > 0)) return {};
  const out: Record<string, number> = {};
  for (const k of keys) {
    const m = /^(v|s)_(.+)_(\d+)$/.exec(k);
    if (!m || k in params) continue;
    const [, pre, type, ns] = m;
    const v0 = params[`${pre}_${type}_0`];
    const r = dist[Number(ns)];
    if (v0 === undefined || !(r > 0)) continue;
    const eta = params[pre === "s" ? `eta_s_${type}` : `eta_${type}`] ?? 2.5;
    out[k] = v0 * Math.pow(env.d0 / r, eta);
  }
  return out;
}

export const FIT_FILE = /(_fit\.json|_fit\.txt|_params\.json|_params\.txt)$/;

// -- step 4: Wannier export, its band check, and the way to Properties ----------------------------------------------
// (Berry curvature, AHC and SHC moved to Properties in M3f; property runs made before stay viewable as PropItems)
export interface PropItem {
  tag: string; label: string; spec: Record<string, unknown>; error?: string; traceback?: string; elapsed?: number;
  summary?: [string, string][]; notes?: string[];
  table?: { headers: string[]; rows: string[][]; n_total: number; step?: number };
  png?: string; dat?: string;
}

export interface PropResult { seed: string; items: PropItem[]; total: number; stopped?: string; unit_hint?: string }
export interface ExportResult { seed: string; kmesh: number[]; files: { tb: string; hr: string; centres: string };
  names: { tb: string; hr: string; centres: string } }

/** Layered cell (long c axis, as the desktop's guess_export_kmesh): 12×12×1 export; otherwise 8×8×8. */
export function isLayered(lattice: number[][]): boolean {
  const len = lattice.map((v) => Math.hypot(...v));
  return len[2] > 2.5 * Math.max(len[0], len[1]);
}

export function exportKmesh(lattice: number[][]): number[] {
  return isLayered(lattice) ? [12, 12, 1] : [8, 8, 8];
}
